/**
 * Renders flower price lines in Florist One order: product, delivery, tax, total.
 *
 * Tax applies to product + delivery — never put Tax above Delivery fee.
 */

import { Divider, Group, Stack, Text } from '@mantine/core';
import { brandColors } from '../../config/theme';
import { formatUsd } from '../../utils/formatUsd';
import { FlowerOrderPriceLineLabels } from '../../constants/flowerOrderPriceLineLabels';
import { buildFlowerPromotionSavingsLabel } from '../../utils/flowerPromotionSavingsCopy';
import { PriceSummaryTotalLine } from '../common/PriceSummaryTotalLine';
import { PriceSummaryQuotedAmountLine } from '../common/PriceSummaryQuotedAmountLine';
import { PriceSummaryTaxLine } from '../common/PriceSummaryTaxLine';

export interface FlowerOrderPriceBreakdownListProps {
  /** Product / catalog / live product amount. */
  productUsd: number;
  /** Label for the product line (e.g. Product vs New product price). */
  productLabel?: string;
  /** Delivery fee when known; omitted when null/undefined. */
  deliveryFeeUsd?: number | null;
  /** Tax when known; omitted when null/undefined. */
  taxUsd?: number | null;
  /** Combined SS promotion credit; shown as negative savings (PBI #1234). */
  savingsUsd?: number | null;
  /** Savings line label; defaults to generic promotion copy when omitted. */
  savingsLabel?: string | null;
  /** Helper text under the savings line (percent on product + tax relief). */
  savingsExplanation?: string | null;
  /** Locked-percent honesty copy for pre-orders (PBI #1235). */
  percentLockHelper?: string | null;
  /**
   * When false, omit savings explanation / percent-lock prose so a parent can
   * render those helpers full-width (e.g. order confirmation shell).
   */
  includePromoHelpers?: boolean;
  /** Grand total charged to the customer. */
  totalUsd: number;
  /** When true, total row shows a loader (#1416). */
  totalLoading?: boolean;
  deliveryFeeLoading?: boolean;
  taxLoading?: boolean;
  /** Label for the total line. */
  totalLabel?: string;
  /** Emphasize the product amount (Price Updated new product). */
  emphasizeProduct?: boolean;
}

/**
 * One amount row in the flower price breakdown.
 */
function AmountRow({
  label,
  amountUsd,
  emphasis,
  dimmed,
  success,
  ariaLabel,
}: {
  label: string;
  amountUsd: number;
  emphasis?: boolean;
  dimmed?: boolean;
  /** Positive savings / credit lines (promotion, tax relief). */
  success?: boolean;
  ariaLabel?: string;
}) {
  return (
    <Group justify="space-between" gap="md" wrap="nowrap">
      <Text
        size="sm"
        fw={emphasis || success ? 600 : undefined}
        c={success ? brandColors.success : dimmed ? 'dimmed' : undefined}
      >
        {label}
      </Text>
      <Text
        size="sm"
        fw={emphasis ? 700 : success ? 600 : 500}
        c={success ? brandColors.success : undefined}
        style={{ flexShrink: 0 }}
        aria-label={ariaLabel}
      >
        {formatUsd(amountUsd)}
      </Text>
    </Group>
  );
}

/**
 * Product → Delivery fee → Tax → Savings → Total for live / persisted flower quotes.
 */
export function FlowerOrderPriceBreakdownList({
  productUsd,
  productLabel = FlowerOrderPriceLineLabels.product,
  deliveryFeeUsd = null,
  taxUsd = null,
  savingsUsd = null,
  savingsLabel = null,
  savingsExplanation = null,
  percentLockHelper = null,
  includePromoHelpers = true,
  totalUsd,
  totalLoading = false,
  deliveryFeeLoading = false,
  taxLoading = false,
  totalLabel = FlowerOrderPriceLineLabels.orderTotal,
  emphasizeProduct = false,
}: FlowerOrderPriceBreakdownListProps) {
  return (
    <Stack gap={4} data-testid="flower-order-price-breakdown">
      <AmountRow
        label={productLabel}
        amountUsd={productUsd}
        emphasis={emphasizeProduct}
      />
      {deliveryFeeUsd != null || deliveryFeeLoading ? (
        deliveryFeeLoading ? (
          <PriceSummaryQuotedAmountLine
            label={FlowerOrderPriceLineLabels.deliveryFee}
            loading
            loadingAriaLabel="Calculating delivery fee"
            dimmed
          />
        ) : (
          <AmountRow
            label={FlowerOrderPriceLineLabels.deliveryFee}
            amountUsd={deliveryFeeUsd ?? 0}
            dimmed
          />
        )
      ) : null}
      {taxUsd != null || taxLoading ? (
        taxLoading ? (
          <PriceSummaryTaxLine loading />
        ) : (
          <AmountRow
            label={FlowerOrderPriceLineLabels.tax}
            amountUsd={taxUsd ?? 0}
            dimmed
          />
        )
      ) : null}
      {savingsUsd != null && savingsUsd > 0 ? (
        <>
          <AmountRow
            label={savingsLabel?.trim() || buildFlowerPromotionSavingsLabel()}
            amountUsd={-savingsUsd}
            success
            ariaLabel={`Promotion savings ${formatUsd(savingsUsd)}`}
          />
          {includePromoHelpers && savingsExplanation ? (
            <Text size="xs" c="dimmed" lh={1.35}>
              {savingsExplanation}
            </Text>
          ) : null}
        </>
      ) : null}
      {includePromoHelpers && percentLockHelper ? (
        <Text size="xs" c="dimmed" lh={1.35}>
          {percentLockHelper}
        </Text>
      ) : null}
      <Divider my={4} />
      <PriceSummaryTotalLine
        loading={totalLoading}
        totalUsd={totalLoading ? null : totalUsd}
        label={totalLabel}
      />
    </Stack>
  );
}
